import styled from "styled-components";
import { SearchInputProps } from "./SearchInput";
import { calcRem } from "styles/theme";
import { FLEX } from "styles/flex";
import { changeTypeToIcon } from "utils/DashboardUtil";
import { COLORS } from "styles/colors";
import { KeywordSearch } from "api/interfaces/dashboardInterface.interface";
import { useCallback, useEffect, useRef, useState } from "react";
import * as mediaQuery from "components/MediaQuery";
import DebounceSearchInput from "./DebounceSearchInput";

interface MapSearchInputProps extends SearchInputProps {
  onClickKeyword: (content: KeywordSearch) => void;
  searchKeywordList: KeywordSearch[];
  onPowerSearch: (value: string) => void;
}

const MapSearchInput = (props: MapSearchInputProps): JSX.Element => {
  const inputRef = useRef<any>(null);

  const [isFocus, setIsFocus] = useState<boolean>(false);

  const highlightWords = useCallback(
    (data: KeywordSearch) => {
      let coloredCode = data.searchedString ? data.searchedString : "";

      if (props.keyword !== undefined && coloredCode !== undefined) {
        coloredCode = coloredCode.replaceAll(
          data.keyword,
          `<span class="highlight">${data.keyword}</span>`
        );
      }
      return coloredCode;
    },
    [props.searchKeywordList]
  );

  useEffect(() => {
    const onClick = (event: any) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        setIsFocus(false);
      }
    };

    document.body.addEventListener("click", onClick);

    return function cleanup() {
      window.removeEventListener("click", onClick);
    };
  }, [inputRef]);

  const onChangeKeyword = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsFocus(true);
    props.onPowerSearch(event.target.value);
  };

  const onClickSearch = (value: string) => {
    setIsFocus(false);
    props.onClickSearch?.(value);
  };

  const { onClickKeyword, searchKeywordList, onPowerSearch, ...rest } = props;

  return (
    <Wrapper ref={inputRef}>
      <DebounceSearchInput
        {...rest}
        map
        onClickSearch={onClickSearch}
        onChange={onChangeKeyword}
        onFocus={() => setIsFocus(true)}
        debounce={300}
        onClear={() => {
          onClickSearch("");
        }}
        data-testid="atoms-input-search-debounce-search-input-change-keyword"
      />

      {isFocus && props.searchKeywordList.length !== 0 && (
        <SearchDropdown>
          {props.searchKeywordList.map((data: KeywordSearch, idx: number) => {
            // console.log(data);
            return (
              <li
                key={`keyword-list-${idx}`}
                onClick={() => {
                  props.onClickKeyword(data);
                  setIsFocus(false);
                }}
                data-testid="atoms-input-search-li-click-keyword"
              >
                <div className="keyword-list-icon">
                  {changeTypeToIcon(data.keywordType)}
                </div>
                <div
                  className="searched-string"
                  dangerouslySetInnerHTML={{
                    __html: highlightWords(data),
                  }}
                ></div>
              </li>
            );
          })}
        </SearchDropdown>
      )}
    </Wrapper>
  );
};

export default MapSearchInput;

const Wrapper = styled.div`
  position: relative;

  .search-input-wrapper {
    input {
      height: 32px;
    }
  }
  ${mediaQuery.isMobile} {
    width: 100%;
    position: absolute;
    top: 100%;
    left: 0;
    padding: 10px;
    .search-input-wrapper,
    input {
      width: 100%;
      max-width: 100%;
    }
  }
`;

const SearchDropdown = styled.ul`
  width: 100%;
  max-height: 420px;
  overflow-y: auto;
  padding: 8px;
  background-color: ${({ theme }) => theme.COLORS.FILTERITEM};
  position: absolute;
  z-index: 667;

  border: 1px solid #abb5c5;
  border-top: 0px;
  border-bottom-left-radius: 9px;
  border-bottom-right-radius: 9px;

  li {
    font-size: ${calcRem(13)};
    color: #828b9b;
    ${FLEX.FlexStartCenter};
    /* gap: 4px; */
    padding: 10px 0;
    cursor: pointer;

    :hover {
      background-color: ${({ theme }) =>
        theme.value === "light"
          ? theme.COLORS.LIGHTGRAYBORDER
          : COLORS.PRIMARY2};
    }

    .keyword-list-icon {
      flex: 1;
      ${FLEX.FlexCenterCenter};
      width: 16px;
      height: 16px;

      svg {
        height: 100%;
        color: ${({ theme }) => theme.COLORS.WHITE};
      }
    }

    .searched-string {
      flex: 4;
      white-space: nowrap;
      width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;

      .highlight {
        color: ${({ theme }) => theme.COLORS.WHITE};
        font-weight: 500;
      }
    }
  }
`;
